import React from "react";
import styled from "@emotion/styled";
import { useTranslations } from "next-intl";
import { useFormikContext, getIn } from "formik";
import { Chip } from "@mui/material";
import {AutoCompleteField} from "@components/auto-complete-field";
import useCities from "custom-hooks/use-cities.hook";
import sortCities from "@helpers/cities.helper";

interface CityOption {
  id: number;
  description: string;
}

interface Props {
  name: string;
  limitTags?: number;
  disableCloseOnSelect?: boolean;
}

const CitiesField: React.FC<Props> = ({
  name,
  limitTags = 2,
  disableCloseOnSelect = true,
}) => {
  const t = useTranslations("formRealestateFilters");
  const { values, setFieldValue } = useFormikContext<any>();
  const value: CityOption[] = Array.isArray(getIn(values, name))
    ? getIn(values, name)
    : [];

  const [search, setSearch] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const { data, isLoading } = useCities({ q: search });

  const options = React.useMemo(() => {
    return data ? sortCities(data) : [];
  }, [data]);

  const handleInputChange = React.useCallback(
    (_event: any, newValue: string, reason: string) => {
      if (reason === "clear" || reason === "reset") {
        setSearch("");
        return;
      }

      if (reason === "input") {
        setSearch(newValue ?? "");
      }
    },
    []
  );

  const handleChange = React.useCallback(
    (_event: any, newValue: CityOption[]) => {
      const lastOption = newValue?.[newValue.length - 1];
      setFieldValue(name, lastOption ? [lastOption] : []);
    },
    [name, setFieldValue]
  );

  const renderTags = React.useCallback(
    (selected: CityOption[], getTagProps: any) => {
      if (isOpen || search.trim().length > 0) {
        return null;
      }

      const limit = typeof limitTags === "number" ? limitTags : selected.length;
      const visible = selected.slice(0, limit);
      const hiddenCount = selected.length - visible.length;

      return (
        <>
          {visible.map((option, index) => (
            <TagChip
              {...getTagProps({ index })}
              key={option.id}
              label={option.description}
              size="small"
              title={option.description}
            />
          ))}
          {hiddenCount > 0 && (
            <TagChip key="more-tags" label={`+${hiddenCount}`} size="small" disabled />
          )}
        </>
      );
    },
    [isOpen, limitTags, search]
  );

  return (
    <StyledAutoComplete
      name={name}
      multiple
      limitTags={limitTags}
      options={options}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={(option: CityOption) => option?.description ?? ""}
      placeholder={t("city")}
      loading={isLoading}
      renderTags={renderTags}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      // disableCloseOnSelect={disableCloseOnSelect}
    />
  );
};

const TagChip = styled(Chip)`
  max-width: 100px;
  height: 26px;
  & .MuiChip-label {
    display: block;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledAutoComplete = styled(AutoCompleteField)`
  fieldset {
    border-radius: 50px;
  }
  .MuiAutocomplete-inputRoot {
    min-height: 46px;
    flex-wrap: nowrap;
    align-items: center;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }
  .MuiAutocomplete-tag {
    margin: 0 4px 0 0;
  }
  input::placeholder {
    color: #000;
    opacity: 1;
    font-weight: 600;
    font-size: 14px;
    padding-left: 10px;
  }
`;

export default CitiesField;
